import React from 'react';
import { Modal, Table, Tabs } from 'antd';

// Detect Apple platform once at module load — used to swap Ctrl→Cmd, Alt→Option
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

interface HotkeyEntry {
  keys: string;
  note?: string; // optional context shown in smaller grey text after the key(s)
  action: string;
}

// ── File & Edit ───────────────────────────────────────────────────────────────
const FILE_EDIT: HotkeyEntry[] = [
  { keys: 'Ctrl + Alt + N',                  action: 'New project' },
  { keys: 'Ctrl + S',                        action: 'Save project (overwrites if saved, else prompts)' },
  { keys: 'Ctrl + O',                        action: 'Open project file (.balls)' },
  { keys: 'Ctrl + Z',                        action: 'Undo' },
  { keys: 'Ctrl + Y / Ctrl + Shift + Z',     action: 'Redo' },
  { keys: 'Ctrl + D',                        action: 'Duplicate selected layer (with keyframes and texture)' },
  { keys: 'Ctrl + V', note: '(stamp tool active, picker closed)', action: 'Paste clipboard image directly as stamp image' },
  { keys: 'Ctrl + V', note: '(any other tool, no text field active)', action: 'Paste image from clipboard to selected layer' },
];

// ── Viewport — Painting & Tools ──────────────────────────────────────────────
const VIEWPORT: HotkeyEntry[] = [
  { keys: 'Click + Drag',                 action: 'Paint stroke on selected layer' },
  { keys: 'Q',                            action: 'Switch to Draw tool' },
  { keys: 'W',                            action: 'Switch to Fill tool' },
  { keys: 'E',                            action: 'Switch to Erase tool' },
  { keys: 'R',                            action: 'Switch to Stamp tool' },
  { keys: 'T',                            action: 'Switch to Text tool' },
  { keys: 'Y',                            action: 'Switch to Blur tool' },
  { keys: 'F',                            action: 'Centre camera on sphere' },
  { keys: '[',                            action: 'Decrease brush size (×0.9)' },
  { keys: ']',                            action: 'Increase brush size (÷0.9)' },
  { keys: 'V', note: '(hold + drag L/R)',                    action: 'Scrub brush opacity (0 – 100%)' },
  { keys: 'B', note: '(hold + drag L/R)',                    action: 'Scrub brush / stamp size; fill tolerance in Fill mode' },
  { keys: 'X', note: '(hold + drag L/R)',                    action: 'Scrub selected layer opacity (0 – 100%)' },
  { keys: 'C', note: '(hold + drag L/R, Stamp / Text only)', action: 'Scrub stamp rotation' },
  { keys: 'C', note: '(hold + drag L/R, Blur tool only)',    action: 'Scrub blur radius (kernel spread)' },
  { keys: 'S',                            action: 'Set keyframe on all channels of selected layer' },
  { keys: 'Click', note: '(Stamp mode)',  action: 'Project stamp onto layer at click point' },
  { keys: 'Shift + Drag',                 action: 'Rotate selected layer (X/Y axes)' },
  { keys: 'Shift + Alt + Drag',           action: 'Translate selected layer (X/Y)' },
  { keys: ',',                            action: 'Toggle Architecture overlay' },
  { keys: '.',                            action: 'Toggle Safety overlay' },
  { keys: 'H',                            action: 'Toggle hotkey reference HUD' },
];

// ── Camera ────────────────────────────────────────────────────────────────────
const CAMERA: HotkeyEntry[] = [
  { keys: 'Alt + Drag',                   action: 'Orbit / rotate camera' },
  { keys: 'Scroll wheel',                 action: 'Zoom camera in / out' },
  { keys: 'Right-drag',                   action: 'Pan camera' },
  { keys: '← / →',                        action: 'Rotate camera 10° around Y axis' },
  { keys: 'F',                            action: 'Centre camera on sphere' },
  { keys: '1 – 9, 0',                     action: 'Snap to predefined camera views 1 – 10' },
];

// ── Layers ────────────────────────────────────────────────────────────────────
const LAYERS: HotkeyEntry[] = [
  { keys: 'F2',                            action: 'Rename selected layer' },
  { keys: 'Ctrl + ↑ / Ctrl + ↓',         action: 'Move selected layer up / down in layer stack' },
  { keys: 'Ctrl + D',                     action: 'Duplicate selected layer' },
  { keys: 'Backspace',                   action: 'Delete selected layer' },
  { keys: 'Alt + Backspace',             action: 'Clear selected layer (erase all painted content)' },
  { keys: 'Shift', note: '(rotation input)', action: 'Force-key rotation channel (AK+ mode)' },
];

// ── Timeline ─────────────────────────────────────────────────────────────────
const TIMELINE: HotkeyEntry[] = [
  { keys: 'Space',                              action: 'Play / Pause' },
  { keys: 'Click',   note: '(ruler / track)',   action: 'Scrub to frame' },  { keys: 'Click',   note: '(frame counter or timecode display)', action: 'Edit: type a frame number or timecode (MM:SS.FF), Enter commits and jumps, Esc cancels' },  { keys: 'Double-click', note: '(track row)',  action: 'Add keyframe at current frame' },
  { keys: 'Double-click', note: '(diamond)',    action: 'Remove keyframe' },
  { keys: 'Drag',    note: '(diamond)',         action: 'Move keyframe to a different frame' },
  { keys: 'Drag',    note: '(empty track area)', action: 'Region-select keyframes in a frame range' },
  { keys: 'Ctrl + C',                               action: 'Copy selected keyframes' },
  { keys: 'Ctrl + V',                               action: 'Paste copied keyframes at playhead' },
  { keys: 'Delete / Backspace',                action: 'Delete selected keyframes' },
  { keys: 'Esc',                                action: 'Clear keyframe selection' },
  { keys: 'I',                                  action: 'Set in point at playhead' },
  { keys: 'O',                                  action: 'Set out point at playhead' },
  { keys: 'Shift + I / Shift + O',             action: 'Clear in / out point' },
  { keys: 'Drag', note: '(in/out bracket on ruler)', action: 'Reposition in / out point' },
];

// ─── Key renderer ────────────────────────────────────────────────────────────
const KBD_STYLE: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 3,
  border: '1px solid #555',
  background: '#2a2a2a',
  color: '#ddd',
  fontSize: 12,
  lineHeight: '18px',
};

function renderKeys(keys: string, note?: string) {
  const display = isMac
    ? keys.replace(/\bCtrl\b/g, 'Cmd').replace(/\bAlt\b/g, 'Option')
    : keys;
  return (
    <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
      {display.split(' / ').map((combo, ci) => (
        <React.Fragment key={ci}>
          {ci > 0 && <span style={{ color: '#666', margin: '0 5px' }}>/</span>}
          {combo.trim().split(' + ').map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: '#666', margin: '0 2px' }}>+</span>}
              <kbd style={KBD_STYLE}>{part}</kbd>
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
      {note && (
        <span style={{ color: '#888', marginLeft: 6, fontFamily: 'sans-serif', fontSize: 11 }}>
          {note}
        </span>
      )}
    </span>
  );
}

const COLUMNS = [
  {
    title: 'Shortcut',
    dataIndex: 'keys',
    key: 'keys',
    width: 270,
    render: (_: string, row: HotkeyEntry) => renderKeys(row.keys, row.note),
  },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
  },
];

function HotkeyTable({ data }: { data: HotkeyEntry[] }) {
  return (
    <Table<HotkeyEntry>
      dataSource={data.map((h, i) => ({ ...h, key: i }))}
      columns={COLUMNS}
      pagination={false}
      size="small"
      rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'hotkey-alt-row'}
    />
  );
}

const TABS = [
  { key: 'file',     label: 'File & Edit', data: FILE_EDIT },
  { key: 'viewport', label: 'Viewport',    data: VIEWPORT },
  { key: 'camera',   label: 'Camera',      data: CAMERA },
  { key: 'layers',   label: 'Layers',      data: LAYERS },
  { key: 'timeline', label: 'Timeline',    data: TIMELINE },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const HotkeyModal: React.FC<Props> = ({ open, onClose }) => (
  <Modal
    title="Keyboard Shortcuts"
    open={open}
    onCancel={onClose}
    footer={null}
    width={660}
  >
    <Tabs
      items={TABS.map((t) => ({
        key: t.key,
        label: t.label,
        children: <HotkeyTable data={t.data} />,
      }))}
    />
  </Modal>
);

export default HotkeyModal;
